import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem, GridRenderCellParams } from '@mui/x-data-grid';
import { arSD } from '@mui/x-data-grid/locales';
import { Add, Edit } from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import DetailDialog from '../components/DetailDialog';
import { FilterBar } from '../components/common';
import { formatDate } from '../lib/dateUtils';

interface Classification {
  id: string;
  category: string;
  field: string;
  specialization: string;
  created_at: string;
  updated_at: string;
}

const ClassificationsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [classifications, setClassifications] = useState<Classification[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<string | number | null>(null);

  useEffect(() => {
    fetchClassifications();
  }, []);

  const fetchClassifications = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('classifications')
        .select('*')
        .order('category', { ascending: true })
        .order('field', { ascending: true })
        .order('specialization', { ascending: true });
      
      if (error) throw error;
      setClassifications(data || []);
    } catch (error) {
      console.error('Error fetching classifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedRecordId(null);
    setDetailDialogOpen(true);
  };

  const handleEdit = (id: string | number) => {
    setSelectedRecordId(id);
    setDetailDialogOpen(true);
  };

  const classificationFields = [
    { 
      name: 'category', 
      label: t('classifications.category'), 
      type: 'text' as const, 
      required: true 
    },
    { 
      name: 'field', 
      label: t('classifications.field'), 
      type: 'text' as const, 
      required: true 
    },
    { 
      name: 'specialization', 
      label: t('classifications.specialization'), 
      type: 'text' as const, 
      required: true 
    },
  ];

  const columns: GridColDef[] = [
    { 
      field: 'category', 
      headerName: t('classifications.category'), 
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      )
    },
    { 
      field: 'field', 
      headerName: t('classifications.field'), 
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value}
          size="small"
          color="secondary"
          variant="outlined"
        />
      )
    },
    { 
      field: 'specialization', 
      headerName: t('classifications.specialization'), 
      flex: 1,
      minWidth: 250
    },
    { 
      field: 'created_at', 
      headerName: t('table.createdAt'), 
      width: 150,
      valueFormatter: (value) => formatDate(value, isRTL ? 'ar' : 'en', 'medium'),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('table.actions'),
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Edit />}
          label={t('table.edit')}
          onClick={() => handleEdit(params.id)}
          showInMenu
        />,
      ],
    },
  ];

  // Get unique categories and fields for filtering
  const uniqueCategories = Array.from(new Set(classifications.map(c => c.category)));
  const uniqueFields = Array.from(new Set(classifications.map(c => c.field)));

  // Filter classifications based on search text, categories, and fields
  const filteredClassifications = classifications.filter((classification) => {
    // Search filter
    const matchesSearch = searchText === '' || 
      Object.values(classification).some((value) =>
        String(value).toLowerCase().includes(searchText.toLowerCase())
      );
    
    // Category filter
    const matchesCategory = selectedCategories.length === 0 || 
      selectedCategories.includes(classification.category);
    
    // Field filter
    const matchesField = selectedFields.length === 0 || 
      selectedFields.includes(classification.field);
    
    return matchesSearch && matchesCategory && matchesField;
  });

  return (
    <Box>
      {/* Page Header with Stats */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
              {t('nav.classifications')}
            </Typography>
            {/* Filter Stats */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                label={`${t('common.total')}: ${classifications.length}`}
                color="primary"
                variant="outlined"
                size="small"
              />
              {filteredClassifications.length !== classifications.length && (
                <Chip
                  label={`${t('common.filtered')}: ${filteredClassifications.length}`}
                  color="success"
                  size="small"
                />
              )}
              {selectedCategories.length > 0 && (
                <Chip
                  label={`${t('classifications.category')}: ${selectedCategories.length} ${t('common.selected')}`}
                  onDelete={() => setSelectedCategories([])}
                  color="info"
                  variant="outlined"
                  size="small"
                />
              )}
              {selectedFields.length > 0 && (
                <Chip
                  label={`${t('classifications.field')}: ${selectedFields.length} ${t('common.selected')}`}
                  onDelete={() => setSelectedFields([])}
                  color="warning"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Box>
          <Button variant="contained" onClick={handleAdd} sx={{ gap: 1 }}>
            <Add sx={{ fontSize: 18 }} />
            {t('table.add')}
          </Button>
        </Box>
      </Box>

      {/* Filter Bar */}
      <FilterBar
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={t('classifications.searchPlaceholder')}
        filters={[
          {
            name: 'categories',
            label: t('classifications.category'),
            options: uniqueCategories.map(category => ({
              value: category,
              label: category
            })),
            value: selectedCategories,
            onChange: setSelectedCategories,
            multiple: true,
          },
          {
            name: 'fields',
            label: t('classifications.field'),
            options: uniqueFields.map(field => ({
              value: field,
              label: field
            })),
            value: selectedFields,
            onChange: setSelectedFields,
            multiple: true,
          },
        ]}
      />

      {/* Data Grid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredClassifications}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          onRowDoubleClick={(params) => handleEdit(params.id)}
          localeText={isRTL ? arSD.components.MuiDataGrid.defaultProps.localeText : undefined}
          sx={{
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        />
      </Box>

      {/* Detail Dialog */}
      <DetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        tableName="classifications"
        recordId={selectedRecordId || undefined}
        onSave={fetchClassifications}
        fields={classificationFields}
      />
    </Box>
  );
};

export default ClassificationsPage;
